import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import Icon from '../common/Icon';
import {
  tenderAwardeeService,
  CreateTenderAwardeeData,
  TenderAwardee,
  UpdateTenderAwardeeData,
} from '../../services/tenderAwardeeService';

export interface AwardeeFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: (awardee: TenderAwardee) => void;
  editing?: TenderAwardee | null;
  initialValues?: Partial<CreateTenderAwardeeData>;
}

const emptyForm = (): CreateTenderAwardeeData => ({
  name: '',
  awardee_city: '',
  awardee_phone: '',
  awardee_email: '',
  notes: '',
});

const AwardeeFormModal: React.FC<AwardeeFormModalProps> = ({
  open,
  onClose,
  onSaved,
  editing,
  initialValues,
}) => {
  const [form, setForm] = useState<CreateTenderAwardeeData>(emptyForm());
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    if (editing) {
      setForm({
        name: editing.name,
        awardee_city: editing.awardee_city || '',
        awardee_phone: editing.awardee_phone || '',
        awardee_email: editing.awardee_email || '',
        notes: editing.notes || '',
      });
    } else {
      setForm({
        ...emptyForm(),
        ...initialValues,
        name: initialValues?.name || '',
      });
    }
  }, [open, editing, initialValues]);

  const handleSave = async () => {
    setError('');
    const name = form.name.trim();
    if (!name) {
      setError('Name is required');
      return;
    }
    const payload: CreateTenderAwardeeData = {
      name,
      awardee_city: form.awardee_city?.trim() || undefined,
      awardee_phone: form.awardee_phone?.trim() || undefined,
      awardee_email: form.awardee_email?.trim() ? form.awardee_email.trim() : null,
      notes: form.notes?.trim() || undefined,
    };
    try {
      setSaving(true);
      let saved: TenderAwardee;
      if (editing) {
        saved = await tenderAwardeeService.update(editing.id, payload as UpdateTenderAwardeeData);
      } else {
        saved = await tenderAwardeeService.create(payload);
      }
      onSaved(saved);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save awardee');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold text-gray-900">
            {editing ? 'Edit awardee' : 'Add awardee'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <Icon component={FiX} className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
              {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">Name *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSave();
                }
              }}
              className="block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">City</label>
            <input
              type="text"
              value={form.awardee_city || ''}
              onChange={(e) => setForm({ ...form, awardee_city: e.target.value })}
              className="block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Phone</label>
              <input
                type="tel"
                value={form.awardee_phone || ''}
                onChange={(e) => setForm({ ...form, awardee_phone: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Email</label>
              <input
                type="email"
                value={form.awardee_email || ''}
                onChange={(e) => setForm({ ...form, awardee_email: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">Notes</label>
            <textarea
              rows={2}
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
            >
              {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AwardeeFormModal;
